import './NavigationTabs.scss';

import React, { useState } from 'react';

import {
  AnchorBar,
  AnchorBarList,
  AnchorBarListItem,
  BoundedContent,
  Box,
  Button,
  Callout,
  FlexBox,
  Hidden,
  Icon,
  Link,
} from '@vp/swan';

import {
  FeatureGroupModel,
  HeroModel,
  TextBlockModel,
} from '@vp/digital-common-data-model-lib';

import {
  GET_STARTED_CTA_DETAIL,
  trackNavigationTabPressed,
  NAVIGATION_TABS_DATA_SECTION_LABEL,
} from '../WixWebsitesAnalytics';
import MissingContentAlert from '../../common/MissingContentAlert';
import SalesBanner from '../../common/salesBanner/SalesBanner';
import useSeasonalSales from '../../common/hooks/useSeasonalSales';

type NavigationTabsProps = {
  content: { nav: HeroModel; salesBanner: TextBlockModel };
  locale: string;
  accessibility: FeatureGroupModel;
  primaryCtaClick: Function;
};

const NavigationTabs = ({
  content,
  locale,
  accessibility,
  primaryCtaClick,
}: NavigationTabsProps) => {
  const [activeAnchor, setActiveAnchor] = useState('features');

  const { isSeasonalSales } = useSeasonalSales();

  if (!content?.nav) {
    return <MissingContentAlert />;
  }

  return (
    <Box className="wix-websites-pdp__navigation-tabs">
      <SalesBanner content={content.salesBanner} locale={locale} />

      <Hidden xs>
        <BoundedContent mb={5} backgroundColor="standard">
          <AnchorBar
            sticky
            onActiveAnchorChanged={setActiveAnchor}
            aria-label={accessibility.features[14].title}
          >
            <AnchorBarList pl={0} pr={0} style={{ minWidth: 'auto' }}>
              <AnchorBarListItem>
                <Link
                  href="#features"
                  aria-current={
                    activeAnchor === 'features' ? 'true' : undefined
                  }
                  onClick={() => {
                    trackNavigationTabPressed('features');
                  }}
                >
                  {content.nav.heroMetadata[2].title}
                </Link>
              </AnchorBarListItem>

              <AnchorBarListItem>
                <Link
                  href="#templates"
                  aria-current={
                    activeAnchor === 'templates' ? 'true' : undefined
                  }
                  onClick={() => {
                    trackNavigationTabPressed('templates');
                  }}
                >
                  {content.nav.heroMetadata[1].title}
                </Link>
              </AnchorBarListItem>

              <AnchorBarListItem>
                <Link
                  href="#key-benefits"
                  aria-current={
                    activeAnchor === 'key-benefits' ? 'true' : undefined
                  }
                  onClick={() => {
                    trackNavigationTabPressed('key-benefits');
                  }}
                >
                  {content.nav.heroMetadata[0].title}
                </Link>
              </AnchorBarListItem>

              <AnchorBarListItem>
                <Link
                  href="#pricing"
                  aria-current={activeAnchor === 'pricing' ? 'true' : undefined}
                  onClick={() => {
                    trackNavigationTabPressed('pricing');
                  }}
                  className="wix-websites-pdp__navigation-bar__pricing"
                >
                  <FlexBox alignItems="center">
                    {content.nav.heroMetadata[5].title}
                    {isSeasonalSales && <Callout ml={3}>50% OFF</Callout>}
                  </FlexBox>
                </Link>
              </AnchorBarListItem>

              <AnchorBarListItem>
                <Link
                  href="#how-it-works"
                  aria-current={
                    activeAnchor === 'how-it-works' ? 'true' : undefined
                  }
                  onClick={() => {
                    trackNavigationTabPressed('how-it-works');
                  }}
                >
                  {content.nav.heroMetadata[3].title}
                </Link>
              </AnchorBarListItem>

              <AnchorBarListItem>
                <Link
                  href="#reviews"
                  aria-current={activeAnchor === 'reviews' ? 'true' : undefined}
                  onClick={() => {
                    trackNavigationTabPressed('reviews');
                  }}
                >
                  {content.nav.heroMetadata[4].title}
                </Link>
              </AnchorBarListItem>

              <AnchorBarListItem>
                <FlexBox alignItems="center" style={{ height: '100%' }}>
                  <Button
                    skin="primary"
                    width="standard"
                    data-position="2"
                    data-translation={GET_STARTED_CTA_DETAIL}
                    data-section={NAVIGATION_TABS_DATA_SECTION_LABEL}
                    className="wix-websites-pdp__get-started__cta"
                    onClick={() => {
                      primaryCtaClick();
                    }}
                  >
                    {content.nav.ctaName}

                    <Icon iconType="arrowRight" />
                  </Button>
                </FlexBox>
              </AnchorBarListItem>
            </AnchorBarList>
          </AnchorBar>
        </BoundedContent>
      </Hidden>
    </Box>
  );
};

export default NavigationTabs;
